if (!customElements.get('cart-progress-bar')) {
  class CartProgressBar extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      
      // Initialize immediately if possible
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }
    
    init() {
      if (this.initialized) return;
      
      console.log('Initializing cart progress bar');
      
      // Initialize all elements
      this.progressBar = this.querySelector('.cart-progress-bar__progress');
      this.discountText = this.querySelector('.cart-progress-bar__discount-text');
      this.markers = this.querySelectorAll('.cart-progress-bar__marker');
      this.markerLabels = this.querySelectorAll('.cart-progress-bar__marker-label');
      
      // Check if all required elements are found
      if (!this.progressBar || !this.discountText || this.markers.length === 0) {
        console.error('Cart progress bar: Required elements not found');
        return; // Don't initialize if required elements are missing
      }
      
      // Mark as initialized only after confirming all elements exist
      this.initialized = true;
      
      // Log if elements are found
      console.log('Progress bar element found:', !!this.progressBar);
      console.log('Discount text element found:', !!this.discountText);
      console.log('Markers found:', this.markers.length);
      console.log('Marker labels found:', this.markerLabels.length);
      
      // Define tier thresholds
      this.tier1Qty = 2;
      this.tier2Qty = 3;
      this.tier3Qty = 5;
      
      // Define discount percentages
      this.tier1Discount = 10;
      this.tier2Discount = 15;
      this.tier3Discount = 25;
      
      // Set up event listeners
      this.setupEventListeners();
      
      // Initial update
      this.updateProgressBar();
    }
    
    setupEventListeners() {
      // Listen for cart update events if the pub/sub system is available
      if (typeof window.PUB_SUB_EVENTS !== 'undefined' && typeof window.subscribe === 'function') {
        console.log('Setting up cart update subscription');
        this.cartUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          console.log('Cart update event received', event);
          this.onCartUpdate(event);
        });
      } else {
        console.warn('PUB_SUB_EVENTS or subscribe not available, falling back to manual updates');
      }
      
      // Also update when the cart drawer is opened
      document.addEventListener('drawer:open', (event) => {
        if (event.detail && event.detail.drawer === 'cart') {
          console.log('Cart drawer opened, updating progress bar');
          this.updateProgressBar();
        }
      });
      
      // Listen for cart form submissions
      const cartForm = document.getElementById('CartDrawer-Form');
      if (cartForm) {
        cartForm.addEventListener('submit', () => {
          setTimeout(() => this.updateProgressBar(), 500);
        });
      }
      
      // Listen for quantity changes
      document.addEventListener('change', (event) => {
        if (event.target.matches('[name="updates[]"]')) {
          setTimeout(() => this.updateProgressBar(), 500);
        }
      });
    }
    
    connectedCallback() {
      console.log('Cart progress bar connected to DOM');
      if (!this.initialized) {
        this.init();
      }
    }
    
    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }
    
    onCartUpdate(event) {
      console.log('Processing cart update event', event);
      if (event && event.cartData) {
        // If we have cart data from the event, use the item count
        const totalQuantity = event.cartData.item_count || 0;
        console.log('Cart update with quantity:', totalQuantity);
        this.renderProgressBar(totalQuantity);
      } else {
        // Otherwise fetch the latest cart data
        this.updateProgressBar();
      }
    }
    
    updateProgressBar(itemCount = null) {
      console.log('Updating progress bar, item count:', itemCount);
      
      // If itemCount is not provided, get it from the cart
      if (itemCount === null) {
        // Try to get cart data from the API
        if (typeof window.fetch === 'function') {
          const cartUrl = '/cart.js';
          console.log('Fetching cart data from:', cartUrl);
          
          fetch(cartUrl)
            .then(response => response.json())
            .then(cart => {
              console.log('Cart data received:', cart);
              const totalQuantity = cart.item_count || 0;
              this.renderProgressBar(totalQuantity);
            })
            .catch(error => {
              console.error('Error fetching cart:', error);
              // Fallback to DOM method
              this.getCartCountFromDOM();
            });
        } else {
          // Fallback to DOM method if fetch is not available
          this.getCartCountFromDOM();
        }
      } else {
        this.renderProgressBar(itemCount);
      }
    }
    
    getCartCountFromDOM() {
      // Try to get the cart count from the DOM
      const cartCountElement = document.querySelector('.cart-count-bubble');
      if (cartCountElement) {
        const cartCount = parseInt(cartCountElement.textContent.trim(), 10) || 0;
        console.log('Cart count from DOM:', cartCount);
        this.renderProgressBar(cartCount);
      } else {
        console.error('Could not determine cart count from DOM');
        // Default to 0 if we can't determine the count
        this.renderProgressBar(0);
      }
    }
    
    renderProgressBar(totalQuantity) {
      console.log('Rendering progress bar with quantity:', totalQuantity);
      
      // Determine current tier and progress
      let currentTier = 0;
      let currentDiscount = 0;
      let nextTierQty = this.tier1Qty;
      let nextDiscount = this.tier1Discount;
      let progressPercentage = 0;
      
      if (totalQuantity >= this.tier3Qty) {
        currentTier = 3;
        currentDiscount = this.tier3Discount;
        nextTierQty = 999; // No next tier
        nextDiscount = 0;
        progressPercentage = 100;
      } else if (totalQuantity >= this.tier2Qty) {
        currentTier = 2;
        currentDiscount = this.tier2Discount;
        nextTierQty = this.tier3Qty;
        nextDiscount = this.tier3Discount;

        // 3 → 5 pairs adds 25 % per pair (50 % → 100 %)
        const extraQty = totalQuantity - this.tier2Qty;
        const extraProgress = extraQty * 25;
        progressPercentage = 50 + extraProgress;
      } else if (totalQuantity >= this.tier1Qty) {
        currentTier = 1;
        currentDiscount = this.tier1Discount;
        nextTierQty = this.tier2Qty;
        nextDiscount = this.tier2Discount;

        // 2 → 3 pairs adds 25 % (25 % → 50 %)
        const extraQty = totalQuantity - this.tier1Qty;
        const extraProgress = extraQty * 25;
        progressPercentage = 25 + extraProgress;
      } else {
        currentTier = 0;
        currentDiscount = 0;
        nextTierQty = this.tier1Qty;
        nextDiscount = this.tier1Discount;

        // 0 → 2 pairs adds 12.5 % per pair (0 % → 25 %)
        progressPercentage = (totalQuantity * 25) / this.tier1Qty;
      }
      
      console.log('Progress calculation:', {
        currentTier,
        currentDiscount,
        nextTierQty,
        nextDiscount,
        progressPercentage
      });
      
      // Update the progress bar width
      if (this.progressBar) {
        this.progressBar.style.width = `${progressPercentage}%`;
      }
      
      // Update marker active states
      if (this.markers) {
        this.markers.forEach((marker, index) => {
          if (index <= currentTier) {
            marker.classList.add('active');
          } else {
            marker.classList.remove('active');
          }
        });
      }
      
      // Update marker label active states
      if (this.markerLabels) {
        this.markerLabels.forEach((label, index) => {
          if (index === currentTier) {
            label.classList.add('active');
          } else {
            label.classList.remove('active');
          }
        });
      }
      
      // Update discount text
      if (this.discountText) {
        if (currentTier === 0) {
          const itemsToAdd = this.tier1Qty - totalQuantity;
          // We'll use the data attributes from the HTML instead of window.theme.strings
          const template = this.dataset.addMoreText || 'Add {{ quantity }} more to get {{ discount }}% discount!';
          this.discountText.textContent = template
            .replace('{{ quantity }}', itemsToAdd)
            .replace('{{ discount }}', this.tier1Discount);
        } else if (currentTier === 3) {
          const template = this.dataset.maxDiscountText || 'You now have the maximum discount: {{ discount }}%!';
          this.discountText.textContent = template
            .replace('{{ discount }}', currentDiscount);
        } else {
          const itemsToAdd = nextTierQty - totalQuantity;
          const template = this.dataset.currentDiscountText || 'You now have {{ discount }}% discount! Add {{ next_quantity }} more to get {{ next_discount }}% discount.';
          this.discountText.textContent = template
            .replace('{{ discount }}', currentDiscount)
            .replace('{{ next_quantity }}', itemsToAdd)
            .replace('{{ next_discount }}', nextDiscount);
        }
      }
    }
  }
  
  customElements.define('cart-progress-bar', CartProgressBar);
}
